// Utilities for parsing and comparing version strings like 25.5.1+35501
// Frontend version format expected: major.minor.patch+build
// Backend provides separate numeric parts (major, minor, release, build) plus versionString

export interface IParsedVersion {
  major: number;
  minor: number;
  patch: number; // 'release' in backend naming
  build: number | null; // may be null if absent
}

/**
 * Parses a frontend version string into its numeric components.
 *
 * The version string should follow the format `major.minor.patch[+build]`, where
 * `major`, `minor`, and `patch` are required numeric values, and `build` is an optional
 * numeric build metadata separated by a plus sign (`+`).
 *
 * @param version - The version string to parse (e.g., "1.2.3+456").
 * @returns An object containing the parsed `major`, `minor`, `patch`, and optional `build` numbers,
 *          or `null` if the input is invalid or cannot be parsed.
 */
export const parseFrontendVersion = (
  version: string | undefined
): IParsedVersion | null => {
  if (!version) return null;
  // Split build metadata
  const [core, buildPart] = version.split('+');
  const parts = core.split('.');
  if (parts.length < 3) return null;
  const [majorStr, minorStr, patchStr] = parts;
  const major = Number(majorStr);
  const minor = Number(minorStr);
  const patch = Number(patchStr);
  const build = buildPart ? Number(buildPart) : null;
  if ([major, minor, patch].some(n => Number.isNaN(n))) return null;
  if (buildPart && Number.isNaN(build)) return null;
  return { major, minor, patch, build };
};

/**
 * Compares two version objects (frontend and backend) and determines their relative order.
 *
 * @param frontend - The frontend version object implementing `IParsedVersion` with `major`, `minor`, and `patch` properties.
 * @param backend - The backend version object with `major`, `minor`, and `release` properties.
 * @returns Returns `-1` if the frontend version is older than the backend version,
 * `0` if the versions are equal, and `1` if the frontend version is newer.
 */
export const compareVersions = (
  frontend: IParsedVersion,
  backend: { major: number; minor: number; release: number }
): number => {
  // returns -1 if frontend is older, 0 if equal, 1 if newer
  if (frontend.major !== backend.major)
    return frontend.major < backend.major ? -1 : 1;
  if (frontend.minor !== backend.minor)
    return frontend.minor < backend.minor ? -1 : 1;
  if (frontend.patch !== backend.release)
    return frontend.patch < backend.release ? -1 : 1;
  return 0;
};

/**
 * Determines if the frontend version is older than the backend version.
 *
 * If the frontend version is unknown (`null`), it is treated as not older (returns `false`).
 *
 * @param frontend - The parsed frontend version, or `null` if unknown.
 * @param backend - The backend version object containing `major`, `minor`, `release`, and `build` numbers.
 * @returns `true` if the frontend version is older than the backend version, `false` if it is equal, newer or not known.
 */
export const isFrontendOlder = (
  frontend: IParsedVersion | null,
  backend: { major: number; minor: number; release: number; build: number }
): boolean => {
  if (!frontend) return false; // treat unknown as stale to force safety reload
  return compareVersions(frontend, backend) === -1;
};
